/**
 * Функции для работы с API-эндпоинтами АСУБП.
 * Все CRUD-операции для всех сущностей системы.
 */
import apiClient from './client';
import type { LoginResponse } from '@/types';
import type {
  UserListParams,
  UserPayload,
  AircraftTypeListParams,
  AircraftTypePayload,
  AircraftListParams,
  AircraftPayload,
  SquadronListParams,
  SquadronPayload,
  PilotListParams,
  PilotPayload,
  AirportListParams,
  AirportPayload,
  RunwayPayload,
  FlightListParams,
  FlightPayload,
  FlightEventPayload,
  FlightProfilePayload,
  FlightParametersPayload,
  EventListParams,
  EventPayload,
  EventCatalogListParams,
  EventCatalogPayload,
  SafetyEventListParams,
  SafetyEventPayload,
  RiskMapListParams,
  RiskMapEntryPayload,
  SafetyEventCatalogListParams,
  SafetyEventCatalogPayload,
  ReportListParams,
  ReportGeneratePayload,
  ReportConstructorPayload,
  NotificationListParams,
  NotificationSubscriptionPayload,
  SyncHistoryListParams,
  SyncMappingsListParams,
  AnalyticsParams,
  ServiceListParams,
  ServicePayload,
  AuditLogListParams,
  AuthJournalListParams,
  RegInfoAnalysisParams,
  RoleDetail,
  PermissionGroup,
  UserPermissionsResponse,
  RoleCreatePayload,
  RoleUpdatePayload,
  RolePermissionsPayload,
  UserPermissionsPayload,
  UserRolesPayload,
} from '@/types/api';

// ============================================================
// Типы пагинированного ответа (соответствует бэкенду)
// ============================================================
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface SuggestItem {
  id: number;
  label: string;
  // Опциональные поля — возвращаются только из suggestPilots для форматирования
  // имени согласно pilot_display_mode ('name' | 'code' | 'both')
  full_name?: string;
  employee_code?: string;
}

// ============================================================
// Авторизация
// ============================================================

export const login = async (
  username: string,
  password: string,
): Promise<LoginResponse> => {
  const response = await apiClient.post('/auth/login/', { username, password });
  const data = response.data;
  return {
    user: data.user,
    tokens: {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    },
  };
};

export const refreshToken = async (
  token: string,
): Promise<{ access_token: string; refresh_token: string }> => {
  const response = await apiClient.post('/auth/refresh/', { refresh_token: token });
  return response.data;
};

export const logout = async (token: string): Promise<void> => {
  await apiClient.post('/auth/logout/', { refresh: token });
};

export const getMe = async () => {
  const response = await apiClient.get('/auth/me/');
  return response.data;
};

// ============================================================
// Пользователи
// ============================================================

export const getUsers = async (params?: UserListParams) => {
  const response = await apiClient.get('/users/', { params });
  return response.data;
};

export const getUser = async (id: number) => {
  const response = await apiClient.get(`/users/${id}/`);
  return response.data;
};

export const createUser = async (data: UserPayload) => {
  const response = await apiClient.post('/users/', data);
  return response.data;
};

export const updateUser = async (id: number, data: UserPayload) => {
  const response = await apiClient.put(`/users/${id}/`, data);
  return response.data;
};

export const deleteUser = async (id: number) => {
  const response = await apiClient.delete(`/users/${id}/`);
  return response.data;
};

export const activateUser = async (id: number) => {
  const response = await apiClient.post(`/users/${id}/activate/`);
  return response.data;
};

export const deactivateUser = async (id: number) => {
  const response = await apiClient.post(`/users/${id}/deactivate/`);
  return response.data;
};

export const unlockUser = async (id: number) => {
  const response = await apiClient.post(`/users/${id}/unlock/`);
  return response.data;
};

export const resetUserPassword = async (id: number, new_password: string) => {
  const response = await apiClient.post(`/users/${id}/reset-password/`, { new_password });
  return response.data;
};

export const exportUsers = async (params?: UserListParams): Promise<Blob> => {
  const response = await apiClient.get('/users/export/', { params, responseType: 'blob' });
  return response.data;
};

// ============================================================
// Универсальный экспорт в Excel
// ============================================================

export interface ExportColumnMeta {
  key: string;
  label: string;
}

export interface ExportSourceMeta {
  key: string;
  title: string;
  date_field: string | null;
  columns: ExportColumnMeta[];
  default_columns: string[];
}

export interface ExportExcelPayload {
  source: string;
  columns?: string[];
  filters?: Record<string, unknown>;
  date_from?: string;
  date_to?: string;
  title?: string;
}

export interface ExportExcelResult {
  blob: Blob;
  filename: string;
}

export const getExportSources = async (): Promise<ExportSourceMeta[]> => {
  const response = await apiClient.get('/export/sources/');
  return response.data;
};

export const exportToExcel = async (payload: ExportExcelPayload): Promise<ExportExcelResult> => {
  const response = await apiClient.post('/export/excel/', payload, { responseType: 'blob' });
  const blob = response.data as Blob;
  const headers = response.headers as Record<string, string> | undefined;
  const cd = (headers?.['content-disposition'] || headers?.['Content-Disposition'] || '') as string;
  const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : `export_${payload.source}.xlsx`;
  return { blob, filename };
};

export const suggestUsers = async (q: string, limit = 200) => {
  const response = await apiClient.get('/users/suggest/', { params: { q, limit } });
  return response.data;
};

export const getRoles = async (): Promise<Array<{ id: number; name: string; display_name: string }>> => {
  const response = await apiClient.get('/roles/');
  return response.data;
};

// ============================================================
// Permission Matrix: роли (детальные) и пер-юзерные права
// ============================================================

export const getRolesDetailed = async (): Promise<RoleDetail[]> => {
  const response = await apiClient.get('/roles/detailed/');
  return response.data;
};

export const getRole = async (id: number): Promise<RoleDetail> => {
  const response = await apiClient.get(`/roles/${id}/`);
  return response.data;
};

export const createRole = async (data: RoleCreatePayload): Promise<RoleDetail> => {
  const response = await apiClient.post('/roles/', data);
  return response.data;
};

export const updateRole = async (id: number, data: RoleUpdatePayload): Promise<RoleDetail> => {
  const response = await apiClient.put(`/roles/${id}/`, data);
  return response.data;
};

export const updateRolePermissions = async (
  id: number,
  data: RolePermissionsPayload,
): Promise<RoleDetail> => {
  const response = await apiClient.put(`/roles/${id}/permissions/`, data);
  return response.data;
};

export const deleteRole = async (id: number): Promise<void> => {
  await apiClient.delete(`/roles/${id}/`);
};

export const getPermissions = async (): Promise<PermissionGroup[]> => {
  const response = await apiClient.get('/permissions/');
  return response.data;
};

export const getUserPermissions = async (userId: number): Promise<UserPermissionsResponse> => {
  const response = await apiClient.get(`/users/${userId}/permissions/`);
  return response.data;
};

export const updateUserPermissions = async (
  userId: number,
  data: UserPermissionsPayload,
): Promise<UserPermissionsResponse> => {
  const response = await apiClient.put(`/users/${userId}/permissions/`, data);
  return response.data;
};

export const updateUserRoles = async (userId: number, data: UserRolesPayload) => {
  const response = await apiClient.put(`/users/${userId}/roles/`, data);
  return response.data;
};

// ============================================================
// Типы воздушных судов
// ============================================================

export const getAircraftTypes = async (params?: AircraftTypeListParams) => {
  const response = await apiClient.get('/aircraft-types/', { params });
  return response.data;
};

export const getAircraftType = async (id: number) => {
  const response = await apiClient.get(`/aircraft-types/${id}/`);
  return response.data;
};

export const createAircraftType = async (data: AircraftTypePayload) => {
  const response = await apiClient.post('/aircraft-types/', data);
  return response.data;
};

export const updateAircraftType = async (id: number, data: AircraftTypePayload) => {
  const response = await apiClient.put(`/aircraft-types/${id}/`, data);
  return response.data;
};

export const deleteAircraftType = async (id: number) => {
  const response = await apiClient.delete(`/aircraft-types/${id}/`);
  return response.data;
};

export const suggestAircraftTypes = async (q: string, limit = 200) => {
  const response = await apiClient.get('/aircraft-types/suggest/', { params: { q, limit } });
  return response.data;
};

// ============================================================
// Воздушные суда
// ============================================================

export const getAircraft = async (params?: AircraftListParams) => {
  const response = await apiClient.get('/aircraft/', { params });
  return response.data;
};

export const getAircraftById = async (id: number) => {
  const response = await apiClient.get(`/aircraft/${id}/`);
  return response.data;
};

export const createAircraft = async (data: AircraftPayload) => {
  const response = await apiClient.post('/aircraft/', data);
  return response.data;
};

export const updateAircraft = async (id: number, data: AircraftPayload) => {
  const response = await apiClient.put(`/aircraft/${id}/`, data);
  return response.data;
};

export const deleteAircraft = async (id: number) => {
  const response = await apiClient.delete(`/aircraft/${id}/`);
  return response.data;
};

export const suggestAircraft = async (q: string, limit = 200) => {
  const response = await apiClient.get('/aircraft/suggest/', { params: { q, limit } });
  return response.data;
};

// ── Регистраторы ВС ──────────────────────────────────────────

export const getAircraftRecorders = async (aircraftId: number) => {
  const response = await apiClient.get(`/aircraft/${aircraftId}/recorders/`);
  return response.data as AircraftRecorder[];
};

export const createAircraftRecorder = async (aircraftId: number, data: Omit<AircraftRecorder, 'id' | 'created_at'>) => {
  const response = await apiClient.post(`/aircraft/${aircraftId}/recorders/`, data);
  return response.data as AircraftRecorder;
};

export const updateAircraftRecorder = async (aircraftId: number, recorderId: number, data: Partial<AircraftRecorder>) => {
  const response = await apiClient.patch(`/aircraft/${aircraftId}/recorders/${recorderId}/`, data);
  return response.data as AircraftRecorder;
};

export const deleteAircraftRecorder = async (aircraftId: number, recorderId: number) => {
  const response = await apiClient.delete(`/aircraft/${aircraftId}/recorders/${recorderId}/`);
  return response.data;
};

export interface AircraftRecorder {
  id: number;
  recorder_type: string;
  serial_number: string;
  installed_at: string | null;
  removed_at: string | null;
  is_active: boolean;
  notes: string;
  created_at: string;
}

// ============================================================
// Эскадрильи
// ============================================================

export const getSquadrons = async (params?: SquadronListParams) => {
  const response = await apiClient.get('/squadrons/', { params });
  return response.data;
};

export const getSquadron = async (id: number) => {
  const response = await apiClient.get(`/squadrons/${id}/`);
  return response.data;
};

export const createSquadron = async (data: SquadronPayload) => {
  const response = await apiClient.post('/squadrons/', data);
  return response.data;
};

export const updateSquadron = async (id: number, data: SquadronPayload) => {
  const response = await apiClient.put(`/squadrons/${id}/`, data);
  return response.data;
};

export const deleteSquadron = async (id: number) => {
  const response = await apiClient.delete(`/squadrons/${id}/`);
  return response.data;
};

export const suggestSquadrons = async (q: string, limit = 200) => {
  const response = await apiClient.get('/squadrons/suggest/', { params: { q, limit } });
  return response.data;
};


// ── Детали эскадрильи ────────────────────────────────────────

export const getSquadronPilots = async (squadronId: number) => {
  const response = await apiClient.get(`/squadrons/${squadronId}/pilots/`);
  return response.data as SquadronPilotItem[];
};

export const getSquadronStats = async (squadronId: number) => {
  const response = await apiClient.get(`/squadrons/${squadronId}/stats/`);
  return response.data as SquadronStats;
};

export const setSquadronCommander = async (squadronId: number, pilotId: number) => {
  const response = await apiClient.post(`/squadrons/${squadronId}/set-commander/`, null, {
    params: { pilot_id: pilotId },
  });
  return response.data;
};

export const transferPilot = async (pilotId: number, data: { target_squadron_id: number; date_from: string; reason?: string }) => {
  const response = await apiClient.post(`/squadrons/pilots/${pilotId}/transfer/`, data);
  return response.data;
};

export const getPilotSquadronHistory = async (pilotId: number) => {
  const response = await apiClient.get(`/squadrons/pilots/${pilotId}/squadron-history/`);
  return response.data as PilotSquadronHistoryItem[];
};

export interface SquadronPilotItem {
  id: number;
  full_name: string;
  employee_code: string;
  position: string;
  is_active: boolean;
}

export interface SquadronStats {
  squadron_id: number;
  squadron_name: string;
  commander: { id: number; full_name: string; employee_code: string } | null;
  total_pilots: number;
  active_pilots: number;
  total_flights: number;
  events_green: number;
  events_yellow: number;
  events_red: number;
}

export interface PilotSquadronHistoryItem {
  id: number;
  squadron_id: number;
  squadron_name: string;
  date_from: string;
  date_to: string | null;
  reason: string;
  transferred_by: string | null;
}

// ============================================================
// Пилоты
// ============================================================

export const getPilots = async (params?: PilotListParams) => {
  const response = await apiClient.get('/pilots/', { params });
  return response.data;
};

export const getPilot = async (id: number) => {
  const response = await apiClient.get(`/pilots/${id}/`);
  return response.data;
};

export const createPilot = async (data: PilotPayload) => {
  const response = await apiClient.post('/pilots/', data);
  return response.data;
};

export const updatePilot = async (id: number, data: PilotPayload) => {
  const response = await apiClient.put(`/pilots/${id}/`, data);
  return response.data;
};

export const deletePilot = async (id: number) => {
  const response = await apiClient.delete(`/pilots/${id}/`);
  return response.data;
};

export const suggestPilots = async (q: string, limit = 200) => {
  const response = await apiClient.get('/pilots/suggest/', { params: { q, limit } });
  return response.data;
};

export const getPilotAttachments = async (pilotId: number) => {
  const response = await apiClient.get(`/pilots/${pilotId}/attachments/`);
  return response.data;
};

export const uploadPilotAttachment = async (pilotId: number, file: File, description = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('description', description);
  const response = await apiClient.post(`/pilots/${pilotId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const downloadPilotAttachment = async (pilotId: number, attachmentId: number) => {
  const response = await apiClient.get(`/pilots/${pilotId}/attachments/${attachmentId}/download/`);
  return response.data as { url: string; filename: string };
};

export const deletePilotAttachment = async (pilotId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/pilots/${pilotId}/attachments/${attachmentId}/`);
  return response.data;
};

// ============================================================
// Аэропорты
// ============================================================

export const getAirports = async (params?: AirportListParams) => {
  const response = await apiClient.get('/airports/', { params });
  return response.data;
};

export const getAirport = async (id: number) => {
  const response = await apiClient.get(`/airports/${id}/`);
  return response.data;
};

export const createAirport = async (data: AirportPayload) => {
  const response = await apiClient.post('/airports/', data);
  return response.data;
};

export const updateAirport = async (id: number, data: AirportPayload) => {
  const response = await apiClient.put(`/airports/${id}/`, data);
  return response.data;
};

export const deleteAirport = async (id: number) => {
  const response = await apiClient.delete(`/airports/${id}/`);
  return response.data;
};

export const suggestAirports = async (q: string, limit = 200) => {
  const response = await apiClient.get('/airports/suggest/', { params: { q, limit } });
  return response.data;
};

// Взлётно-посадочные полосы
export const getRunways = async (airportId: number) => {
  const response = await apiClient.get(`/airports/${airportId}/runways/`);
  return response.data;
};

export const createRunway = async (airportId: number, data: RunwayPayload) => {
  const response = await apiClient.post(`/airports/${airportId}/runways/`, data);
  return response.data;
};

export const updateRunway = async (airportId: number, runwayId: number, data: RunwayPayload) => {
  const response = await apiClient.put(`/airports/${airportId}/runways/${runwayId}/`, data);
  return response.data;
};

export const deleteRunway = async (airportId: number, runwayId: number) => {
  const response = await apiClient.delete(`/airports/${airportId}/runways/${runwayId}/`);
  return response.data;
};

// ============================================================
// Полёты
// ============================================================

export const getFlights = async (params?: FlightListParams) => {
  const response = await apiClient.get('/flights/', { params });
  return response.data;
};

export const getFlight = async (id: number) => {
  const response = await apiClient.get(`/flights/${id}/`);
  return response.data;
};

export const createFlight = async (data: FlightPayload) => {
  const response = await apiClient.post('/flights/', data);
  return response.data;
};

export const updateFlight = async (id: number, data: FlightPayload) => {
  const response = await apiClient.put(`/flights/${id}/`, data);
  return response.data;
};

export const deleteFlight = async (id: number) => {
  const response = await apiClient.delete(`/flights/${id}/`);
  return response.data;
};

export const getFlightEvents = async (flightId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/events/`);
  return response.data;
};

export const getFlightProfile = async (flightId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/profile/`);
  return response.data;
};

export const getFlightParameters = async (flightId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/parameters/`);
  return response.data;
};

// Flight Events CRUD
export const createFlightEvent = async (flightId: number, data: FlightEventPayload) => {
  const response = await apiClient.post(`/flights/${flightId}/events/`, data);
  return response.data;
};

export const updateFlightEvent = async (flightId: number, eventId: number, data: FlightEventPayload) => {
  const response = await apiClient.put(`/flights/${flightId}/events/${eventId}/`, data);
  return response.data;
};

export const deleteFlightEvent = async (flightId: number, eventId: number) => {
  const response = await apiClient.delete(`/flights/${flightId}/events/${eventId}/`);
  return response.data;
};

// Flight Profile CRUD
export const createFlightProfile = async (flightId: number, data: FlightProfilePayload) => {
  const response = await apiClient.post(`/flights/${flightId}/profile/`, data);
  return response.data;
};

export const updateFlightProfile = async (flightId: number, profileId: number, data: FlightProfilePayload) => {
  const response = await apiClient.put(`/flights/${flightId}/profile/${profileId}/`, data);
  return response.data;
};

export const deleteFlightProfile = async (flightId: number, profileId: number) => {
  const response = await apiClient.delete(`/flights/${flightId}/profile/${profileId}/`);
  return response.data;
};

// Flight Parameters upsert
export const updateFlightParameters = async (flightId: number, data: FlightParametersPayload) => {
  const response = await apiClient.put(`/flights/${flightId}/parameters/`, data);
  return response.data;
};

// ============================================================
// Авиационные события (полётные)
// ============================================================

export const getEvents = async (params?: EventListParams) => {
  const response = await apiClient.get('/events/', { params });
  return response.data;
};

export const createEvent = async (data: EventPayload) => {
  const response = await apiClient.post('/events/', data);
  return response.data;
};

export const updateEvent = async (id: number, data: EventPayload) => {
  const response = await apiClient.put(`/events/${id}/`, data);
  return response.data;
};

export const deleteEvent = async (id: number) => {
  const response = await apiClient.delete(`/events/${id}/`);
  return response.data;
};

// Каталог событий (events)
export const getEventsCatalog = async (params?: EventCatalogListParams) => {
  const response = await apiClient.get('/events/catalog/', { params });
  return response.data;
};

export const createEventCatalogEntry = async (data: EventCatalogPayload) => {
  const response = await apiClient.post('/events/catalog/', data);
  return response.data;
};

export const updateEventCatalogEntry = async (id: number, data: EventCatalogPayload) => {
  const response = await apiClient.put(`/events/catalog/${id}/`, data);
  return response.data;
};

export const deleteEventCatalogEntry = async (id: number) => {
  const response = await apiClient.delete(`/events/catalog/${id}/`);
  return response.data;
};

// ============================================================
// События безопасности
// ============================================================

export const getSafetyEvents = async (params?: SafetyEventListParams) => {
  const response = await apiClient.get('/safety/', { params });
  return response.data;
};

export const getSafetyEvent = async (id: number) => {
  const response = await apiClient.get(`/safety/${id}/`);
  return response.data;
};

export const createSafetyEvent = async (data: SafetyEventPayload) => {
  const response = await apiClient.post('/safety/', data);
  return response.data;
};

export const updateSafetyEvent = async (id: number, data: SafetyEventPayload) => {
  const response = await apiClient.put(`/safety/${id}/`, data);
  return response.data;
};

export const deleteSafetyEvent = async (id: number) => {
  const response = await apiClient.delete(`/safety/${id}/`);
  return response.data;
};

export const transitionSafetyEvent = async (id: number, newState: string) => {
  const response = await apiClient.post(`/safety/${id}/transition/`, { new_state: newState });
  return response.data;
};

// Import safety events from XLSX
export const downloadImportTemplate = async () => {
  const response = await apiClient.get('/safety/import/template/', { responseType: 'blob' });
  const blob = new Blob([response.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'import_template.xlsx';
  a.click();
  URL.revokeObjectURL(url);
};

export const importSafetyEvents = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/safety/import/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getSafetyEventComments = async (eventId: number) => {
  const response = await apiClient.get(`/safety/${eventId}/comments/`);
  return response.data;
};

export const addSafetyEventComment = async (eventId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/safety/${eventId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

export const getFlightComments = async (flightId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/comments/`);
  return response.data;
};

export const addFlightComment = async (flightId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/flights/${flightId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

export const getFlightEventComments = async (eventId: number) => {
  const response = await apiClient.get(`/events/${eventId}/comments/`);
  return response.data;
};

export const addFlightEventComment = async (eventId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/events/${eventId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

export const getFlightEventAttachments = async (eventId: number) => {
  const response = await apiClient.get(`/events/${eventId}/attachments/`);
  return response.data;
};

export const uploadFlightEventAttachment = async (eventId: number, file: File, description = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('description', description);
  const response = await apiClient.post(`/events/${eventId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const downloadFlightEventAttachment = async (eventId: number, attachmentId: number) => {
  const response = await apiClient.get(`/events/${eventId}/attachments/${attachmentId}/download/`);
  return response.data as { url: string; filename: string };
};

export const deleteFlightEventAttachment = async (eventId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/events/${eventId}/attachments/${attachmentId}/`);
  return response.data;
};

export const getRiskMapEntryComments = async (entryId: number) => {
  const response = await apiClient.get(`/risk-maps/${entryId}/comments/`);
  return response.data;
};

export const addRiskMapEntryComment = async (entryId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/risk-maps/${entryId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

// --- Комментарии: Воздушные суда ---

export const getAircraftComments = async (aircraftId: number) => {
  const response = await apiClient.get(`/aircraft/${aircraftId}/comments/`);
  return response.data;
};

export const addAircraftComment = async (aircraftId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/aircraft/${aircraftId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

// --- Комментарии: Типы ВС ---

export const getAircraftTypeComments = async (aircraftTypeId: number) => {
  const response = await apiClient.get(`/aircraft-types/${aircraftTypeId}/comments/`);
  return response.data;
};

export const addAircraftTypeComment = async (aircraftTypeId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/aircraft-types/${aircraftTypeId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

// --- Комментарии: Аэропорты ---

export const getAirportComments = async (airportId: number) => {
  const response = await apiClient.get(`/airports/${airportId}/comments/`);
  return response.data;
};

export const addAirportComment = async (airportId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/airports/${airportId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

// --- Комментарии: Лётный состав ---

export const getPilotComments = async (pilotId: number) => {
  const response = await apiClient.get(`/pilots/${pilotId}/comments/`);
  return response.data;
};

export const addPilotComment = async (pilotId: number, text: string, parentId?: number) => {
  const response = await apiClient.post(`/pilots/${pilotId}/comments/`, { text, parent_id: parentId ?? null });
  return response.data;
};

// ============================================================
// Карта рисков
// ============================================================

export const getRiskMapEntries = async (params?: RiskMapListParams) => {
  const response = await apiClient.get('/risk-maps/', { params });
  return response.data;
};

export const createRiskMapEntry = async (data: RiskMapEntryPayload) => {
  const response = await apiClient.post('/risk-maps/', data);
  return response.data;
};

export const updateRiskMapEntry = async (id: number, data: RiskMapEntryPayload) => {
  const response = await apiClient.put(`/risk-maps/${id}/`, data);
  return response.data;
};

export const deleteRiskMapEntry = async (id: number) => {
  const response = await apiClient.delete(`/risk-maps/${id}/`);
  return response.data;
};

export const getRiskSeverityReference = async () => {
  const response = await apiClient.get('/risk-maps/severity-reference/');
  return response.data;
};

// ============================================================
// Каталог событий безопасности
// ============================================================

export const getSafetyEventCatalog = async (params?: SafetyEventCatalogListParams) => {
  const response = await apiClient.get('/event-catalog/', { params });
  return response.data;
};

export const createSafetyEventCatalogEntry = async (data: SafetyEventCatalogPayload) => {
  const response = await apiClient.post('/event-catalog/', data);
  return response.data;
};

export const updateSafetyEventCatalogEntry = async (id: number, data: SafetyEventCatalogPayload) => {
  const response = await apiClient.put(`/event-catalog/${id}/`, data);
  return response.data;
};

export const deleteSafetyEventCatalogEntry = async (id: number) => {
  const response = await apiClient.delete(`/event-catalog/${id}/`);
  return response.data;
};

// ============================================================
// Отчёты
// ============================================================

export const getReportTemplates = async () => {
  const response = await apiClient.get('/reports/templates/');
  return response.data;
};

export const generateReport = async (data: ReportGeneratePayload) => {
  const response = await apiClient.post('/reports/generate/', data);
  return response.data;
};

export const getReportStatus = async (runId: number) => {
  const response = await apiClient.get(`/reports/status/${runId}/`);
  return response.data;
};

export const getReportHistory = async (params?: ReportListParams) => {
  const response = await apiClient.get('/reports/history/', { params });
  return response.data;
};

export const constructReportPreview = async (data: ReportConstructorPayload) => {
  const response = await apiClient.post('/reports/construct/preview/', data);
  return response.data;
};

export const constructReportGenerate = async (data: ReportConstructorPayload) => {
  const response = await apiClient.post('/reports/construct/generate/', data);
  return response.data;
};

export const downloadReport = async (reportId: number) => {
  const response = await apiClient.get(`/reports/download/${reportId}/`, {
    responseType: 'blob',
  });
  const contentDisposition = response.headers['content-disposition'] || '';
  const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : `report_${reportId}`;
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ============================================================
// Уведомления
// ============================================================

export const getNotifications = async (params?: NotificationListParams) => {
  const response = await apiClient.get('/notifications/', { params });
  return response.data;
};

export const getUnreadCount = async () => {
  const response = await apiClient.get('/notifications/unread-count/');
  return response.data;
};

export const markNotificationRead = async (id: number) => {
  const response = await apiClient.post(`/notifications/${id}/read/`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await apiClient.post('/notifications/read-all/');
  return response.data;
};

export const getNotificationSubscriptions = async () => {
  const response = await apiClient.get('/notifications/subscriptions/');
  return response.data;
};

export const createNotificationSubscription = async (data: NotificationSubscriptionPayload) => {
  const response = await apiClient.post('/notifications/subscriptions/', data);
  return response.data;
};

export const deleteNotificationSubscription = async (id: number) => {
  const response = await apiClient.delete(`/notifications/subscriptions/${id}/`);
  return response.data;
};

// ============================================================
// Синхронизация
// ============================================================

export const getSyncStatus = async () => {
  const response = await apiClient.get('/sync/status/');
  return response.data;
};

export const triggerSync = async () => {
  const response = await apiClient.post('/sync/trigger/');
  return response.data;
};

export const getSyncHistory = async (params?: SyncHistoryListParams) => {
  const response = await apiClient.get('/sync/history/', { params });
  return response.data;
};

export const getSyncRun = async (runId: number) => {
  const response = await apiClient.get(`/sync/history/${runId}/`);
  return response.data;
};

export const getSyncMappings = async (params?: SyncMappingsListParams) => {
  const response = await apiClient.get('/sync/mappings/', { params });
  return response.data;
};

// ============================================================
// Аналитика (дашборд)
// ============================================================

export const getDashboardSummary = async () => {
  const response = await apiClient.get('/analytics/dashboard/summary/');
  return response.data;
};

export const getFlightStats = async (params?: AnalyticsParams) => {
  const response = await apiClient.get('/analytics/dashboard/flight-stats/', { params });
  return response.data;
};

export const getSafetyTrends = async (params?: AnalyticsParams) => {
  const response = await apiClient.get('/analytics/dashboard/safety-trends/', { params });
  return response.data;
};

export const getRiskDistribution = async () => {
  const response = await apiClient.get('/analytics/dashboard/risk-distribution/');
  return response.data;
};

export const getPilotPerformance = async (params?: AnalyticsParams) => {
  const response = await apiClient.get('/analytics/dashboard/pilot-performance/', { params });
  return response.data;
};

export const getFleetUtilization = async (params?: AnalyticsParams) => {
  const response = await apiClient.get('/analytics/dashboard/fleet-utilization/', { params });
  return response.data;
};

export const getMyFlights = async (pilotId?: number) => {
  const params = pilotId ? { pilot_id: pilotId } : {};
  const response = await apiClient.get('/analytics/dashboard/my-flights/', { params });
  return response.data;
};

// ============================================================
// Аналитика — пресеты и конструктор (Phase C)
// ============================================================

export interface AnalyticsPresetMeta {
  key: string;
  label: string;
  description?: string;
  source?: string;
  default_chart?: string;
}

export interface AnalyticsDimensionMeta {
  key: string;
  label: string;
}

export interface AnalyticsMetricMeta {
  key: string;
  label: string;
  source?: string;
  note?: string;
}

export interface AnalyticsResultItem {
  key: string;
  value: number;
}

export interface AnalyticsResult {
  preset_key?: string;
  label?: string;
  dimension?: string;
  metric?: string;
  source?: string;
  chart_type?: string;
  items: AnalyticsResultItem[];
}

export interface AnalyticsRunParams {
  date_from?: string;
  date_to?: string;
  aircraft_type_id?: number;
  aircraft_id?: number;
  service_id?: number;
  limit?: number;
}

export interface AnalyticsQueryParams extends AnalyticsRunParams {
  dimension: string;
  metric: string;
}

export const getAnalyticsPresets = async (): Promise<AnalyticsPresetMeta[]> => {
  const response = await apiClient.get('/analytics/presets/');
  const data = response.data;
  // Бэкенд может вернуть массив или { items, count }
  if (Array.isArray(data)) return data as AnalyticsPresetMeta[];
  return (data?.items ?? []) as AnalyticsPresetMeta[];
};

export const getAnalyticsDimensions = async (): Promise<AnalyticsDimensionMeta[]> => {
  const response = await apiClient.get('/analytics/dimensions/');
  const data = response.data;
  if (Array.isArray(data)) return data as AnalyticsDimensionMeta[];
  return (data?.items ?? []) as AnalyticsDimensionMeta[];
};

export const getAnalyticsMetrics = async (): Promise<AnalyticsMetricMeta[]> => {
  const response = await apiClient.get('/analytics/metrics/');
  const data = response.data;
  if (Array.isArray(data)) return data as AnalyticsMetricMeta[];
  return (data?.items ?? []) as AnalyticsMetricMeta[];
};

export const runAnalyticsPreset = async (
  presetKey: string,
  params?: AnalyticsRunParams,
): Promise<AnalyticsResult> => {
  const response = await apiClient.get(`/analytics/preset/${presetKey}/`, { params });
  return response.data as AnalyticsResult;
};

export const runAnalyticsQuery = async (
  params: AnalyticsQueryParams,
): Promise<AnalyticsResult> => {
  const response = await apiClient.get('/analytics/query/', { params });
  return response.data as AnalyticsResult;
};

// ============================================================
// Вложения к событиям безопасности
// ============================================================

export const getSafetyEventAttachments = async (eventId: number) => {
  const response = await apiClient.get(`/safety/${eventId}/attachments/`);
  return response.data;
};

export const uploadSafetyEventAttachment = async (eventId: number, file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await apiClient.post(`/safety/${eventId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const downloadSafetyEventAttachment = async (eventId: number, attachmentId: number) => {
  const response = await apiClient.get(`/safety/${eventId}/attachments/${attachmentId}/download/`);
  return response.data;
};

export const deleteSafetyEventAttachment = async (eventId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/safety/${eventId}/attachments/${attachmentId}/`);
  return response.data;
};

// ============================================================
// Вложения к карте рисков
// ============================================================

export const getRiskMapAttachments = async (entryId: number) => {
  const response = await apiClient.get(`/risk-maps/${entryId}/attachments/`);
  return response.data;
};

export const uploadRiskMapAttachment = async (entryId: number, file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await apiClient.post(`/risk-maps/${entryId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const downloadRiskMapAttachment = async (entryId: number, attachmentId: number) => {
  const response = await apiClient.get(`/risk-maps/${entryId}/attachments/${attachmentId}/download/`);
  return response.data;
};

export const deleteRiskMapAttachment = async (entryId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/risk-maps/${entryId}/attachments/${attachmentId}/`);
  return response.data;
};

// ============================================================
// Вложения к полётам
// ============================================================

export const getFlightAttachments = async (flightId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/attachments/`);
  return response.data;
};

export const uploadFlightAttachment = async (flightId: number, file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await apiClient.post(`/flights/${flightId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const downloadFlightAttachment = async (flightId: number, attachmentId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/attachments/${attachmentId}/download/`);
  return response.data;
};

export const deleteFlightAttachment = async (flightId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/flights/${flightId}/attachments/${attachmentId}/`);
  return response.data;
};

// ============================================================
// Службы
// ============================================================

export const getServices = async (params?: ServiceListParams) => {
  const response = await apiClient.get('/services/', { params });
  return response.data;
};

export const getService = async (id: number) => {
  const response = await apiClient.get(`/services/${id}/`);
  return response.data;
};

export const createService = async (data: ServicePayload) => {
  const response = await apiClient.post('/services/', data);
  return response.data;
};

export const updateService = async (id: number, data: ServicePayload) => {
  const response = await apiClient.put(`/services/${id}/`, data);
  return response.data;
};

export const deleteService = async (id: number) => {
  const response = await apiClient.delete(`/services/${id}/`);
  return response.data;
};

export const suggestServices = async (q: string, limit = 200) => {
  const response = await apiClient.get('/services/suggest/', { params: { q, limit } });
  return response.data;
};

export const getServiceCategories = async (id: number) => {
  const response = await apiClient.get(`/services/${id}/categories/`);
  return response.data;
};

export const updateServiceCategories = async (id: number, categories: string[]) => {
  const response = await apiClient.put(`/services/${id}/categories/`, { categories });
  return response.data;
};

// ==================== Extended Params Schema ====================

export const getExtendedParamsSchema = async () => {
  const response = await apiClient.get('/flights/extended-params-schema/');
  return response.data;
};

// ==================== AI Assistant ====================

export interface AskAIResponse {
  answer: string;
  sources: string[];
}

export const askAI = async (question: string): Promise<AskAIResponse> => {
  const response = await apiClient.post('/ai/ask/', { question });
  return response.data;
};

// ==================== Impersonation ====================

export const impersonateUser = async (userId: number) => {
  const response = await apiClient.post(`/auth/impersonate/${userId}/`);
  return response.data;
};

export const stopImpersonation = async () => {
  const response = await apiClient.post('/auth/stop-impersonate/');
  return response.data;
};

// ============================================================
// Журнал аудита
// ============================================================

export const getAuditLog = async (params?: AuditLogListParams) => {
  const response = await apiClient.get('/audit-log/', { params });
  return response.data;
};

// ============================================================
// Журнал авторизаций
// ============================================================

export const getAuthJournal = async (params?: AuthJournalListParams) => {
  const response = await apiClient.get('/auth-journal/', { params });
  return response.data;
};

export const getAuthJournalStats = async (params?: AuthJournalListParams) => {
  const response = await apiClient.get('/auth-journal/stats/', { params });
  return response.data;
};

// ============================================================
// История изменений
// ============================================================

export const getSafetyEventHistory = async (eventId: number) => {
  const response = await apiClient.get(`/safety/${eventId}/history/`);
  return response.data;
};

export const getFlightHistory = async (flightId: number) => {
  const response = await apiClient.get(`/flights/${flightId}/history/`);
  return response.data;
};

// ============================================================
// Параметрический анализ RegInfo
// ============================================================

export const getRegInfoSchema = async () => {
  const response = await apiClient.get('/flights/reginfo-schema/');
  return response.data as Array<{
    key: string;
    label: string;
    group: string;
    type: string;
    unit: string;
  }>;
};

export interface RegInfoResultRow {
  flight_id: number;
  flight_date: string;
  flight_number: string;
  aircraft: string;
  aircraft_id: number;
  aircraft_type: string;
  aircraft_type_id: number | null;
  departure: string;
  arrival: string;
  captain: string;
  captain_id: number | null;
  squadron: string;
  param_values: Record<string, number | null>;
}

export const getRegInfoAnalysis = async (params: RegInfoAnalysisParams) => {
  const response = await apiClient.get('/flights/reginfo-analysis/', { params });
  return response.data as {
    items: RegInfoResultRow[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
    param_labels: Record<string, string>;
  };
};

// ============================================================
// Системные настройки
// ============================================================

export interface SystemSettingsData {
  organization_name: string;
  organization_logo_url: string;
  organization_address: string;
  organization_phone: string;
  primary_color: string;
  pilot_display_mode: 'name' | 'code' | 'both';
  airport_display_mode: 'icao' | 'iata' | 'both';
  shift_start_hour: number;
  shift_start_minute: number;
  shift_end_hour: number;
  shift_end_minute: number;
  mdb_file_path: string;
  mdb_sync_schedule: string;
  mdb_sync_enabled: boolean;
  anomaly_thresholds: Array<{
    param: string;
    aircraft_type_id: number;
    warning: number;
    critical: number;
  }>;
}

export const getSystemSettings = async (): Promise<SystemSettingsData> => {
  const response = await apiClient.get('/settings/system/');
  return response.data;
};

export const updateSystemSettings = async (data: Partial<SystemSettingsData>) => {
  const response = await apiClient.patch('/settings/system/', data);
  return response.data;
};

export const uploadSystemLogo = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/settings/system/logo/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data as { url: string };
};

// ============================================================
// Состояние системы
// ============================================================

export const getSystemStatus = async () => {
  const response = await apiClient.get('/settings/status/');
  return response.data;
};

// ============================================================
// SLA KPI
// ============================================================

export const getSlaKpi = async (params?: {
  service_id?: number;
  date_from?: string;
  date_to?: string;
}) => {
  const response = await apiClient.get('/analytics/dashboard/sla-kpi/', { params });
  return response.data as {
    total: number;
    closed: number;
    overdue: number;
    on_time: number;
    on_time_pct: number;
    closure_rate: number;
    avg_days: number | null;
  };
};

// ============================================================
// Статистика пилота
// ============================================================

export const getPilotStats = async (pilotId: number, year?: number) => {
  const response = await apiClient.get(`/pilots/${pilotId}/stats/`, {
    params: year ? { year } : {},
  });
  return response.data;
};

// ============================================================
// Асинхронный импорт событий безопасности
// ============================================================

export const importSafetyEventsAsync = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/safety/import/async/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data as { task_id: string | null; status: string; error?: string };
};

export const getImportStatus = async (taskId: string) => {
  const response = await apiClient.get(`/safety/import/${taskId}/status/`);
  return response.data as {
    task_id: string;
    status: string;
    result: { total: number; created: number; errors: { row: number; error: string }[] } | null;
  };
};

// ============================================================
// Вложения: ВС (Aircraft)
// ============================================================

export const getAircraftAttachments = async (aircraftId: number) => {
  const response = await apiClient.get(`/aircraft/${aircraftId}/attachments/`);
  return response.data;
};
export const uploadAircraftAttachment = async (aircraftId: number, file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await apiClient.post(`/aircraft/${aircraftId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
export const downloadAircraftAttachment = async (aircraftId: number, attachmentId: number) => {
  const response = await apiClient.get(`/aircraft/${aircraftId}/attachments/${attachmentId}/download/`);
  return response.data;
};
export const deleteAircraftAttachment = async (aircraftId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/aircraft/${aircraftId}/attachments/${attachmentId}/`);
  return response.data;
};

// ============================================================
// Вложения: Тип ВС (AircraftType)
// ============================================================

export const getAircraftTypeAttachments = async (typeId: number) => {
  const response = await apiClient.get(`/aircraft-types/${typeId}/attachments/`);
  return response.data;
};
export const uploadAircraftTypeAttachment = async (typeId: number, file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await apiClient.post(`/aircraft-types/${typeId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
export const downloadAircraftTypeAttachment = async (typeId: number, attachmentId: number) => {
  const response = await apiClient.get(`/aircraft-types/${typeId}/attachments/${attachmentId}/download/`);
  return response.data;
};
export const deleteAircraftTypeAttachment = async (typeId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/aircraft-types/${typeId}/attachments/${attachmentId}/`);
  return response.data;
};

// ============================================================
// Вложения: Аэропорт (Airport)
// ============================================================

export const getAirportAttachments = async (airportId: number) => {
  const response = await apiClient.get(`/airports/${airportId}/attachments/`);
  return response.data;
};
export const uploadAirportAttachment = async (airportId: number, file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await apiClient.post(`/airports/${airportId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
export const downloadAirportAttachment = async (airportId: number, attachmentId: number) => {
  const response = await apiClient.get(`/airports/${airportId}/attachments/${attachmentId}/download/`);
  return response.data;
};
export const deleteAirportAttachment = async (airportId: number, attachmentId: number) => {
  const response = await apiClient.delete(`/airports/${airportId}/attachments/${attachmentId}/`);
  return response.data;
};
